import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Download, Star } from 'lucide-react';
import { SiGoogleplay, SiApple } from 'react-icons/si';
import { projects, personalProjects } from '../data/mock';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const Projects = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  return (
    <section id="projects" className="section-padding bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">Production Apps</h2>
          <p className="section-subtitle mx-auto">
            Mobile systems built for scale, serving thousands of users in production
          </p>
        </motion.div>

        {/* Projects Grid - 2 columns on desktop */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} navigate={navigate} />
          ))}
        </motion.div>

        {/* Personal Projects Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16"
        >
          <div className="text-center mb-16">
            <h2 className="section-title">Personal Projects</h2>
            <p className="section-subtitle mx-auto">
              Experimental and learning projects showcasing architecture patterns and new technologies
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {personalProjects.map((project) => (
              <ProjectCard key={project.id} project={project} navigate={navigate} />
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const ProjectCard = ({ project, navigate }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  // Check if it's a personal project (no store links, has github)
  const isPersonalProject = project.githubUrl && !project.playStoreUrl && !project.appStoreUrl;

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{ y: -8 }}
      onClick={() => navigate(`/project/${project.id}`)}
    >
      <Card className="bg-[#1a1a1a] border-[#2a2a2a] hover:border-[#404040] overflow-hidden cursor-pointer group h-full transition-all duration-300">
                {/* Horizontal Layout: Image/GIF on left, content on right */}
                <div className="flex flex-col md:flex-row">
                  {/* Thumbnail - With or without phone frame */}
                  <div className="relative md:w-2/5 bg-[#0a0a0a] overflow-hidden flex-shrink-0 flex items-center justify-center py-6 px-4">
                    {isPersonalProject ? (
                      // No phone frame for personal projects - just the image
                      <div className="relative w-full h-full">
                        <img
                          src={project.thumbnail}
                          alt={project.name}
                          className="w-full h-full object-cover rounded-lg transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    ) : (
                      // Phone frame for production apps
                      <div className="relative max-w-[290px] w-full mx-auto">
                        {/* Phone border with notch - Slim bezels */}
                        <div className="relative bg-[#1a1a1a] rounded-[2.5rem] p-1.5 border border-[#2a2a2a] shadow-2xl">
                          {/* Notch */}
                          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/4 h-4 bg-[#0a0a0a] rounded-b-xl z-10"></div>
                          
                          {/* Screen */}
                          <div className="relative bg-black rounded-[2.3rem] overflow-hidden">
                            <img
                              src={project.thumbnail}
                              alt={project.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content - Takes 60% width on desktop */}
                  <div className="p-6 md:w-3/5 flex flex-col justify-between">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="mb-3">
                        <h3 className="text-xl font-bold mb-1 text-white group-hover:text-gray-200 transition-colors">
                          {project.name}
                        </h3>
                        <p className="text-xs text-gray-500 monospace font-medium">
                          {project.tagline}
                        </p>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-gray-400 mb-3 leading-relaxed line-clamp-3">
                        {project.description}
                      </p>

                      {/* Metrics */}
                      {project.downloads !== 'N/A' && (
                        <div className="flex items-center gap-4 mb-3 text-sm">
                          <div className="flex items-center gap-2 text-gray-400">
                            <Download size={16} />
                            <span className="font-semibold text-xs">{project.downloads}</span>
                          </div>
                          {project.rating !== 'N/A' && (
                            <div className="flex items-center gap-2 text-gray-400">
                              <Star size={16} fill="currentColor" />
                              <span className="font-semibold text-xs">{project.rating}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tech Stack */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {project.techStack.slice(0, 3).map((tech) => (
                          <Badge key={tech} className="bg-[#2a2a2a] text-gray-300 border-0 hover:bg-[#353535] transition-colors text-xs px-2 py-0.5">
                            {tech}
                          </Badge>
                        ))}
                        {project.techStack.length > 3 && (
                          <Badge className="bg-[#2a2a2a] text-gray-400 border-0 text-xs px-2 py-0.5">
                            +{project.techStack.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Bottom Section - Store Badges and View Details */}
                    <div className="space-y-3 pt-3 border-t border-[#2a2a2a]">
                      {/* Store Badges */}
                      <div className="flex items-center gap-2">
                        {project.githubUrl && (
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(project.githubUrl, '_blank');
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#24292e] hover:bg-[#1b1f23] text-white rounded-lg text-xs font-semibold transition-colors store-badge"
                          >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
                            </svg>
                            <span>GitHub</span>
                          </motion.button>
                        )}
                        {project.playStoreUrl && project.playStoreUrl !== '' && (
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (project.playStoreUrl !== '#') {
                                window.open(project.playStoreUrl, '_blank');
                              }
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#01875f] hover:bg-[#019f6e] text-white rounded-lg text-xs font-semibold transition-colors store-badge"
                          >
                            <SiGoogleplay size={12} />
                            <span>Play Store</span>
                          </motion.button>
                        )}
                        {project.appStoreUrl && project.appStoreUrl !== '' && (
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (project.appStoreUrl !== '#') {
                                window.open(project.appStoreUrl, '_blank');
                              }
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-lg text-xs font-semibold transition-colors store-badge"
                          >
                            <SiApple size={12} />
                            <span>App Store</span>
                          </motion.button>
                        )}
                        {(!project.playStoreUrl || project.playStoreUrl === '') && (!project.appStoreUrl || project.appStoreUrl === '') && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="monospace">Personal Project</span>
                          </div>
                        )}
                      </div>

                      {/* View Details Link */}
                      <div className="flex items-center gap-2 text-sm text-gray-400 group-hover:text-white transition-colors">
                        <span className="font-medium">View Case Study</span>
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          )
        };

export default Projects;
